// Orquestador de la lógica de partida. SIN dependencias de Three.js ni del DOM.
// Es la única fuente de verdad del estado; la presentación se sincroniza por eventos.
import { Board } from './Board.js';
import { Player } from './Player.js';
import { WordValidator } from './WordValidator.js';
import { ScoreManager } from './ScoreManager.js';
import { WordFamilyManager } from './WordFamilyManager.js';
import { WordEvolutionManager } from './WordEvolutionManager.js';
import { MoveManager } from './MoveManager.js';
import { buildBag, drawTiles, DEF_BY_ID } from './TileBag.js';
import { createTileInstance, makeLetterTile, makeFragmentTile } from './Tile.js';
import aiNames from '../data/ai-names-es.json';
import aiFace1 from '../assets/avatar-ia-1.webp';
import aiFace2 from '../assets/avatar-ia-2.webp';
import aiFace3 from '../assets/avatar-ia-3.webp';
import aiFace4 from '../assets/avatar-ia-4.webp';
import { createRNG, deepClone, shuffleInPlace } from '../utils/helpers.js';

// Caras de la IA (4 avatares). Se asigna una al azar a cada rival IA.
// Cada cara lleva su género para asignarle un nombre coherente (hombre/mujer).
const AI_FACES = [
  { img: aiFace1, gender: 'male' },
  { img: aiFace2, gender: 'female' },
  { img: aiFace3, gender: 'male' },
  { img: aiFace4, gender: 'female' },
];
import { normalizeForLookup, letterCount } from '../utils/unicode.js';
import { EVENTS, GAME_MODE } from '../utils/constants.js';

export class Game {
  /**
   * @param {object} deps { bus, config }
   */
  constructor({ bus, config }) {
    this.bus = bus;
    this.config = config;
    this.validator = new WordValidator({ minWordLength: config.rules.minWordLength });
    this.familyManager = new WordFamilyManager();
    this.evolutionManager = new WordEvolutionManager(this.familyManager);
    this.scoreManager = new ScoreManager(config.scoring);
    this.moveManager = new MoveManager();

    this.state = null; // estado serializable
    this.board = null; // instancia Board (envoltorio de state.board)
    this.players = []; // instancias Player
    this.selfPlayerId = null; // id del jugador local (multijugador); null = un solo dispositivo
    this.selfIsHost = null; // rol en red: true=anfitrión (índice 0), false=invitado (índice 1); null=local
  }

  /** Jugador local: en multijugador el que corresponde a este dispositivo. */
  get selfPlayer() {
    if (this.selfPlayerId) {
      const byId = this.players.find((p) => p.id === this.selfPlayerId);
      if (byId) return byId;
      // Red de seguridad: si el id no casa (p. ej. reconexión con id nuevo), NO caigas a
      // players[0] a ciegas —eso convertiría al invitado en anfitrión y le robaría el
      // "TU TURNO"—. Usa el rol: anfitrión = índice 0, invitado = índice 1.
      if (this.selfIsHost != null) return this.players[this.selfIsHost ? 0 : 1] || this.players[0];
    }
    return this.players.find((p) => !p.isAI) || this.players[0];
  }

  /** ¿Le toca al jugador local? */
  isSelfTurn() {
    return this.state && !this.state.finished && this.activePlayer?.id === this.selfPlayer?.id;
  }

  // ---------------------------------------------------------------------------
  // Creación / carga
  // ---------------------------------------------------------------------------

  newGame({ mode = GAME_MODE.CLASSIC, aiLevel = 'normal', numAI = 1, seed = 1234, playerName = 'Tú', playerAvatar = '🙂', playerAvatarImage = null, timerSeconds = 0, allowedTypes = null } = {}) {
    const rng = createRNG(seed);
    const bag = buildBag(rng, { allowedTypes });

    // Jugadores: 1 humano + N IA con personas variadas (nombre + avatar).
    const players = [new Player({ id: 'p0', name: playerName, isAI: false, avatar: playerAvatar, avatarImage: playerAvatarImage })];
    // Caras al azar (sin repetir) y nombres del género que corresponde a cada cara.
    const faces = shuffleInPlace([...AI_FACES], rng);
    const maleNames = shuffleInPlace([...aiNames.male], rng);
    const femaleNames = shuffleInPlace([...aiNames.female], rng);
    let mi = 0, fi = 0;
    for (let i = 0; i < numAI; i++) {
      const face = faces[i % faces.length];
      const name = face.gender === 'male'
        ? maleNames[mi++ % maleNames.length]
        : femaleNames[fi++ % femaleNames.length];
      players.push(new Player({
        id: `ai${i + 1}`,
        name,
        isAI: true,
        aiLevel,
        avatar: 'custom',
        avatarImage: face.img,
      }));
    }

    // Reparto inicial.
    for (const p of players) {
      p.rack.addMany(drawTiles(bag, this.config.rules.rackSize));
    }

    this.players = players;
    this.board = new Board([]);

    this.state = {
      seed,
      mode,
      aiLevel,
      rngState: seed, // referencia (el RNG se recrea de forma determinista si hace falta)
      players: players.map((p) => p.serialize()),
      activePlayerIndex: 0,
      bag,
      board: this.board.words,
      history: [],
      timer: { enabled: timerSeconds > 0, seconds: timerSeconds, remaining: timerSeconds },
      finished: false,
      winnerId: null,
      discovered: {},
      awardedFamilies: {},
      evolutionChain: 0,
      consecutivePasses: 0, // pases seguidos sin jugar (para detectar bloqueo)
      turnStart: null,
    };
    for (const p of players) {
      this.state.discovered[p.id] = [];
      this.state.awardedFamilies[p.id] = [];
    }

    this.bus.emit(EVENTS.GAME_START, { state: this.state });
    this.beginTurn();
    return this.state;
  }

  /** Crea una partida MULTIJUGADOR: solo humanos, a partir del roster de la sala. */
  newNetworkGame({ players: roster, seed = 1234, timerSeconds = 0 } = {}) {
    const rng = createRNG(seed);
    const bag = buildBag(rng);
    const players = roster.map((r, i) => new Player({
      id: r.id, name: r.name, isAI: false, avatar: r.avatar || (i === 0 ? '👑' : '🙂'),
    }));
    for (const p of players) p.rack.addMany(drawTiles(bag, this.config.rules.rackSize));

    this.players = players;
    this.board = new Board([]);
    this.state = {
      seed, mode: GAME_MODE.CLASSIC, aiLevel: null, rngState: seed,
      players: players.map((p) => p.serialize()),
      activePlayerIndex: 0, bag, board: this.board.words, history: [],
      timer: { enabled: timerSeconds > 0, seconds: timerSeconds, remaining: timerSeconds },
      finished: false, winnerId: null, discovered: {}, awardedFamilies: {},
      evolutionChain: 0, consecutivePasses: 0, turnStart: null, network: true,
    };
    for (const p of players) { this.state.discovered[p.id] = []; this.state.awardedFamilies[p.id] = []; }

    this.bus.emit(EVENTS.GAME_START, { state: this.state });
    this.beginTurn();
    return this.state;
  }

  /** Aplica un estado recibido por red (cliente invitado: espejo del anfitrión). */
  applyRemoteState(data) {
    this.state = deepClone(data);
    this.players = this.state.players.map((p) => Player.deserialize(p));
    this.board = new Board(this.state.board);
    this.bus.emit(EVENTS.STATE_CHANGED, { state: this.state });
    this.bus.emit(EVENTS.BOARD_CHANGED, { state: this.state });
  }

  /** Serializa la partida completa para guardar. */
  serialize() {
    // Sincroniza instancias -> state.
    this.state.players = this.players.map((p) => p.serialize());
    this.state.board = this.board.words;
    return deepClone(this.state);
  }

  /** Restaura una partida desde datos serializados. */
  load(data) {
    this.state = deepClone(data);
    this.players = this.state.players.map((p) => Player.deserialize(p));
    this.board = new Board(this.state.board);
    // Reconstruimos el snapshot del turno en curso.
    this.moveManager.begin(this._snapshot());
    this.bus.emit(EVENTS.GAME_START, { state: this.state, resumed: true });
    this.bus.emit(EVENTS.STATE_CHANGED, { state: this.state });
    return this.state;
  }

  // ---------------------------------------------------------------------------
  // Accesores
  // ---------------------------------------------------------------------------

  get activePlayer() {
    return this.players[this.state.activePlayerIndex];
  }

  isHumanTurn() {
    return !this.activePlayer.isAI;
  }

  // ---------------------------------------------------------------------------
  // Turno
  // ---------------------------------------------------------------------------

  beginTurn() {
    const player = this.activePlayer;
    // Estado de referencia para detectar jugadas nuevas / evoluciones / reutilización.
    this.state.turnStart = {
      rackIds: player.rack.tiles.map((t) => t.instanceId),
      validWords: this._currentValidWordTexts(),
      positions: this._boardPositionSignature(),
    };
    this.moveManager.begin(this._snapshot());
    this.bus.emit(EVENTS.TURN_START, {
      playerId: player.id,
      isAI: player.isAI,
      state: this.state,
    });
    this._emitState();
  }

  /** Coloca una ficha de la mano en el tablero. wordId null => nueva palabra en pos (x,z). */
  placeTileFromRack(instanceId, wordId = null, index = -1, pos = null) {
    const player = this.activePlayer;
    if (!player.rack.has(instanceId)) return false;
    this.moveManager.record(this._snapshot());
    const tile = player.rack.remove(instanceId);
    if (wordId === null) {
      this.board.createWord([tile], pos?.x, pos?.z);
    } else {
      this.board.insertTile(wordId, tile, index);
    }
    this._afterMove();
    return true;
  }

  /** Mueve una ficha del tablero a otra palabra / nueva palabra en pos (x,z). */
  moveTileOnBoard(instanceId, targetWordId, index = -1, pos = null) {
    this.moveManager.record(this._snapshot());
    const tile = this.board.removeTile(instanceId);
    if (!tile) return false;
    if (targetWordId === null) this.board.createWord([tile], pos?.x, pos?.z);
    else this.board.insertTile(targetWordId, tile, index);
    this.board.pruneEmpty();
    this._afterMove();
    return true;
  }

  /** Reposiciona una palabra entera en la mesa (arrastre estilo Rummikub). */
  moveWord(wordId, x, z) {
    this.moveManager.record(this._snapshot());
    const ok = this.board.moveWord(wordId, x, z);
    this._afterMove();
    return ok;
  }

  /** Devuelve una ficha del tablero a la mano (antes de confirmar). */
  returnTileToRack(instanceId) {
    // Solo puede volver a la mano si se colocó este turno (estaba en la mano al empezar).
    if (!this.state.turnStart.rackIds.includes(instanceId)) return false;
    this.moveManager.record(this._snapshot());
    const tile = this.board.removeTile(instanceId);
    if (!tile) return false;
    this.activePlayer.rack.add(tile);
    this.board.pruneEmpty();
    this._afterMove();
    return true;
  }

  /**
   * Devuelve a la mano las fichas colocadas ESTE turno que forman palabras INCORRECTAS,
   * dejando en el tablero las palabras correctas. (Al confirmar: las buenas se quedan,
   * las malas vuelven a tu mano.) No usa el historial de deshacer: es un saneado interno.
   */
  _returnInvalidTilesToRack() {
    const playedThisTurn = new Set(this.state.turnStart.rackIds);
    for (const word of [...this.board.words]) {
      if (this.validator.validateWord(word.tiles).valid) continue;
      for (const t of [...word.tiles]) {
        if (!playedThisTurn.has(t.instanceId)) continue; // no tocar fichas de turnos previos
        const tile = this.board.removeTile(t.instanceId);
        if (tile) this.activePlayer.rack.add(tile);
      }
    }
    this.board.pruneEmpty();
  }

  // ---------------------------------------------------------------------------
  // Comodines de acción: dividir / fusionar / transformar
  // ---------------------------------------------------------------------------

  /** Consume un comodín, esté en la mano del jugador o ya colocado en la mesa. */
  _consumeWildcard(wildcardId) {
    if (!wildcardId) return true;
    const removed = this.activePlayer.rack.remove(wildcardId) || this.board.removeTile(wildcardId);
    if (removed) this.board.pruneEmpty();
    return !!removed;
  }

  /**
   * DIVIDIR: convierte una ficha (sílaba/prefijo/sufijo/fragmento) en sus letras.
   * Ej: CA -> C + A. Consume el comodín "dividir".
   * @param {string} targetId ficha a dividir (en el tablero)
   * @param {string} wildcardId comodín dividir (en la mano); opcional en pruebas
   */
  splitTile(targetId, wildcardId = null) {
    const word = this.board.findWordContaining(targetId);
    if (!word) return { ok: false, reason: 'La ficha a dividir debe estar en la mesa.' };
    const idx = word.tiles.findIndex((t) => t.instanceId === targetId);
    const tile = word.tiles[idx];
    if (!tile.canSplit || !tile.splitInto || tile.splitInto.length < 2) {
      return { ok: false, reason: 'Esta ficha no se puede dividir.' };
    }
    this.moveManager.record(this._snapshot());
    const letters = tile.splitInto.map((ch) => makeLetterTile(ch));
    word.tiles.splice(idx, 1, ...letters);
    this._consumeWildcard(wildcardId);
    this._afterMove();
    return { ok: true, letters };
  }

  /**
   * FUSIONAR: une un rango contiguo de fichas de una palabra en un único fragmento.
   * Ej: C + I + Ó + N -> CIÓN. Consume el comodín "fusionar".
   * @param {string[]} targetIds fichas contiguas (>=2) de la MISMA palabra
   */
  mergeTiles(targetIds, wildcardId = null) {
    if (!targetIds || targetIds.length < 2) return { ok: false, reason: 'Selecciona 2 o más fichas.' };
    const word = this.board.findWordContaining(targetIds[0]);
    if (!word) return { ok: false, reason: 'Las fichas deben estar en la mesa.' };
    const indices = targetIds.map((id) => word.tiles.findIndex((t) => t.instanceId === id));
    if (indices.some((i) => i === -1)) return { ok: false, reason: 'Las fichas deben estar en la misma palabra.' };
    indices.sort((a, b) => a - b);
    // Comprobar contigüidad.
    for (let i = 1; i < indices.length; i++) {
      if (indices[i] !== indices[i - 1] + 1) return { ok: false, reason: 'Las fichas deben ser contiguas.' };
    }
    this.moveManager.record(this._snapshot());
    const start = indices[0];
    const end = indices[indices.length - 1];
    const slice = word.tiles.slice(start, end + 1);
    const text = slice.map((t) => t.text).join('');
    const value = slice.reduce((s, t) => s + (t.value || 0), 0);
    const fragment = makeFragmentTile(text, { value });
    word.tiles.splice(start, slice.length, fragment);
    this._consumeWildcard(wildcardId);
    this._afterMove();
    return { ok: true, fragment };
  }

  /**
   * TRANSFORMAR: reemplaza una ficha por una equivalente de su lista de transformaciones.
   * Ej: CA <-> CO. Consume el comodín "transformar".
   */
  transformTile(targetId, newText, wildcardId = null) {
    const tile = this.board.allTiles().find((t) => t.instanceId === targetId)
      || this.activePlayer.rack.find(targetId);
    if (!tile) return { ok: false, reason: 'Ficha no encontrada.' };
    if (!tile.transforms || !tile.transforms.includes(newText)) {
      return { ok: false, reason: 'Transformación no válida para esta ficha.' };
    }
    this.moveManager.record(this._snapshot());
    tile.text = newText;
    tile.splitInto = Array.from(newText);
    this._afterMove();
    this._consumeWildcard(wildcardId);
    this._emitState();
    return { ok: true, tile };
  }

  /** Opciones de transformación disponibles para una ficha. */
  transformOptions(targetId) {
    const tile = this.board.allTiles().find((t) => t.instanceId === targetId)
      || this.activePlayer.rack.find(targetId);
    return tile?.transforms || [];
  }

  /** Asigna el valor de un comodín universal. */
  assignWildcard(instanceId, assignedText) {
    const tile = this.board.allTiles().find((t) => t.instanceId === instanceId)
      || this.activePlayer.rack.find(instanceId);
    if (!tile) return false;
    this.moveManager.record(this._snapshot());
    tile.assignedText = (assignedText || '').toUpperCase(); // se muestra en MAYÚSCULA como el resto
    this._afterMove();
    return true;
  }

  undo() {
    const snap = this.moveManager.undo(this._snapshot());
    if (!snap) return false;
    this._restore(snap);
    this.bus.emit(EVENTS.MOVE_UNDONE, {});
    this._afterMove(false);
    return true;
  }

  /** ¿Hay algo que DESHACER este turno? Funciona también en el INVITADO online, que no tiene
   *  historial local (aplica el estado del host): lo deduce del estado sincronizado (hay fichas
   *  colocadas este turno si la mano actual tiene menos fichas que al empezar el turno). */
  canUndoTurn() {
    if (this.moveManager.canUndo()) return true; // local / host: historial real
    const p = this.activePlayer;
    const startLen = this.state?.turnStart?.rackIds?.length ?? 0;
    return !!p && startLen > 0 && p.rack.size < startLen;
  }

  redo() {
    const snap = this.moveManager.redo(this._snapshot());
    if (!snap) return false;
    this._restore(snap);
    this._afterMove(false);
    return true;
  }

  resetTurn() {
    const snap = this.moveManager.reset();
    this._restore(snap);
    this._afterMove(false);
    return true;
  }

  /**
   * Valida el estado actual del tablero (en tiempo real).
   * @returns {{valid:boolean, results:Array, firstError?:object, changed:boolean}}
   */
  validateCurrentBoard() {
    const res = this.validator.validateBoard(this.board.words);
    res.changed = this._boardChangedThisTurn();
    return res;
  }

  /** Confirma el turno si es válido. Devuelve {ok, breakdown?} o {ok:false, reason}. */
  /** ¿La jugada actual es válida y se puede CONFIRMAR ya? (sin efectos secundarios).
   *  Sirve para avisar al jugador (pulso en el botón CONTINUAR) de que puede pulsarlo. */
  canConfirm() {
    if (!this.state || this.state.finished) return false;
    if (!this._boardChangedThisTurn()) return false;
    if (this._playedTilesThisTurn().length === 0 && this._consumedFromHandCount() === 0) return false;
    if (this.validator.validateBoard(this.board.words).valid) return true;
    // Con palabras incorrectas mezcladas: se permite confirmar si queda al menos una palabra
    // VÁLIDA que use una ficha jugada este turno (las incorrectas volverán a la mano).
    return this._hasValidHandPlay();
  }

  /** ¿Hay al menos una palabra VÁLIDA en el tablero que use una ficha jugada este turno? */
  _hasValidHandPlay() {
    const played = new Set(this._playedTilesThisTurn().map((t) => t.instanceId));
    if (played.size === 0) return false;
    for (const w of this.board.words) {
      if (!this.validator.validateWord(w.tiles).valid) continue;
      if (w.tiles.some((t) => played.has(t.instanceId))) return true;
    }
    return false;
  }

  confirmTurn() {
    // 1) Debe haber cambios y usar al menos una ficha de la mano.
    if (!this._boardChangedThisTurn()) {
      return { ok: false, reason: 'No has realizado ningún cambio.' };
    }
    // Usar un comodín (dividir/fusionar/transformar) también cuenta como jugar de la mano.
    if (this._playedTilesThisTurn().length === 0 && this._consumedFromHandCount() === 0) {
      return { ok: false, reason: 'Debes usar al menos una ficha de tu mano.' };
    }

    // 2) Las palabras CORRECTAS se quedan en el tablero; las INCORRECTAS devuelven sus fichas
    //    (las colocadas este turno) a la mano. Si tras el saneado el tablero es válido y aún
    //    queda jugada, el turno se confirma con las palabras buenas. Si no se puede confirmar
    //    limpiamente (p. ej. no queda ninguna palabra válida), se deja el tablero intacto.
    let validation = this.validator.validateBoard(this.board.words);
    let returnedInvalid = false;
    if (!validation.valid) {
      const before = this._snapshot();
      this._returnInvalidTilesToRack();
      validation = this.validator.validateBoard(this.board.words);
      const stillPlays = this._boardChangedThisTurn()
        && (this._playedTilesThisTurn().length > 0 || this._consumedFromHandCount() > 0);
      if (!validation.valid || !stillPlays) {
        this._restore(before);
        const v = this.validator.validateBoard(this.board.words);
        this.bus.emit(EVENTS.TURN_INVALID, v.firstError);
        return { ok: false, reason: v.firstError.reason, validation: v };
      }
      returnedInvalid = true;
    }

    // 3) Puntuación (con el tablero ya saneado).
    const playedTiles = this._playedTilesThisTurn();
    const currValidWords = this._currentValidWordTexts();
    const prevValidWords = this.state.turnStart.validWords;
    const prevNorm = new Set(prevValidWords.map((w) => normalizeForLookup(w)));
    const newWords = currValidWords.filter((w) => !prevNorm.has(normalizeForLookup(w)));
    const evolutions = this.evolutionManager.detectEvolutions(prevValidWords, currValidWords);
    const reusedTiles = this._reusedTileCount();
    // Ganar = quedarse sin fichas NORMALES; los comodines sobrantes no cuentan.
    const rackEmptied = this.activePlayer.rack.isEffectivelyEmpty();

    // Familias completadas por este jugador.
    const player = this.activePlayer;
    const discovered = new Set(this.state.discovered[player.id].map((w) => normalizeForLookup(w)));
    for (const w of currValidWords) discovered.add(normalizeForLookup(w));
    const awarded = new Set(this.state.awardedFamilies[player.id]);
    const completedFamilies = this.familyManager
      .completedFamilies(discovered)
      .filter((f) => !awarded.has(f.id));

    // Cadena de evoluciones.
    if (evolutions.length > 0) this.state.evolutionChain += evolutions.length;
    else this.state.evolutionChain = 0;

    const breakdown = this.scoreManager.computeTurnScore({
      playedTiles,
      reusedTiles,
      newWords,
      evolutions,
      rackEmptied,
      familiesCompleted: completedFamilies,
      evolutionChain: this.state.evolutionChain,
    });

    // 4) Aplicar resultados.
    player.addScore(breakdown.total);
    this.state.discovered[player.id] = [...discovered];
    for (const f of completedFamilies) {
      this.state.awardedFamilies[player.id].push(f.id);
      this.bus.emit(EVENTS.FAMILY_COMPLETED, { family: f, playerId: player.id });
    }
    for (const ev of evolutions) this.bus.emit(EVENTS.WORD_EVOLVED, ev);

    // Registro en el historial.
    this.state.history.push({
      playerId: player.id,
      newWords,
      evolutions,
      score: breakdown.total,
      breakdown,
    });

    // 5) Reponer atril.
    if (this.config.rules.refillRack && !rackEmptied) {
      const need = this.config.rules.rackSize - player.rack.size;
      if (need > 0) player.rack.addMany(drawTiles(this.state.bag, need));
    }

    // 6) ¿Fin de partida? Termina en cuanto un jugador se queda SIN fichas en la mano.
    if (rackEmptied) {
      player.finished = true;
    }

    // Jugada válida: reinicia el contador de pases seguidos.
    this.state.consecutivePasses = 0;

    this.bus.emit(EVENTS.TURN_CONFIRMED, { playerId: player.id, breakdown, newWords, evolutions });
    this.bus.emit(EVENTS.SCORE_UPDATED, { playerId: player.id, score: player.score });

    const ended = this._checkEndGame();
    if (!ended) this._nextPlayer();
    return { ok: true, breakdown, newWords, evolutions, familiesCompleted: completedFamilies, ended, returnedInvalid };
  }

  /** Roba una ficha (acción de turno): coge 1 del saco y pasa el turno. */
  drawAndPass() {
    return this.passTurn(true);
  }

  /** Pasa el turno (revierte cambios no confirmados). Si fromDraw, roba 1 ficha. */
  passTurn(fromDraw = false) {
    this.resetTurn(); // primero revierte cualquier jugada sin confirmar
    // Robar DESPUÉS del reset para que la ficha nueva no se descarte.
    if (fromDraw && this.state.bag.length > 0) {
      this.activePlayer.rack.addMany(drawTiles(this.state.bag, 1));
      this.bus.emit(EVENTS.TILE_DRAWN, { playerId: this.activePlayer.id });
    }
    this.state.consecutivePasses += 1;
    this.bus.emit(EVENTS.TURN_PASSED, { playerId: this.activePlayer.id, fromDraw });
    const ended = this._checkEndGame();
    if (!ended) this._nextPlayer();
    return { ok: true, ended };
  }

  /**
   * Se acabó el tiempo del turno sin que el jugador confirmara. Resolución "amable":
   *  - Se contabilizan las palabras VÁLIDAS que dejó puestas (se puntúan).
   *  - Las fichas colocadas este turno que NO forman parte de una palabra válida
   *    vuelven a la mano.
   *  - Si no formó ninguna palabra válida, se le añade 1 ficha a la mano.
   * Devuelve {ok, played, breakdown?, ...} para el feedback en pantalla.
   */
  resolveTimeout() {
    // Fichas que pertenecen a alguna palabra VÁLIDA de la mesa.
    const validWordTileIds = new Set();
    for (const w of this.board.words) {
      if (this.validator.validateWord(w.tiles).valid) {
        for (const t of w.tiles) validWordTileIds.add(t.instanceId);
      }
    }
    // Devuelve a la mano las fichas colocadas este turno que NO están en palabra válida.
    for (const t of this._playedTilesThisTurn()) {
      if (!validWordTileIds.has(t.instanceId)) this.returnTileToRack(t.instanceId);
    }
    // ¿Queda una jugada válida (palabra nueva) que puntuar?
    const validation = this.validator.validateBoard(this.board.words);
    const usedHand = this._playedTilesThisTurn().length > 0 || this._consumedFromHandCount() > 0;
    if (validation.valid && usedHand && this._boardChangedThisTurn()) {
      const res = this.confirmTurn();
      if (res.ok) return { ...res, timedOut: true, played: true };
    }
    // Sin palabras válidas: devuelve todo y añade 1 ficha a la mano (y pasa el turno).
    this.drawAndPass();
    return { ok: true, timedOut: true, played: false };
  }

  // ---------------------------------------------------------------------------
  // Internos
  // ---------------------------------------------------------------------------

  /** Finaliza la partida inmediatamente (usado por modos en solitario). */
  endNow() {
    this.resetTurn();
    this._endGame();
  }

  _nextPlayer() {
    const n = this.players.length;
    let idx = this.state.activePlayerIndex;
    for (let i = 0; i < n; i++) {
      idx = (idx + 1) % n;
      if (!this.players[idx].finished) break;
    }
    this.state.activePlayerIndex = idx;
    this.beginTurn();
  }

  _snapshot() {
    return {
      board: deepClone(this.board.words),
      rack: deepClone(this.activePlayer.rack.tiles),
    };
  }

  _restore(snap) {
    this.board.words.length = 0;
    for (const w of snap.board) this.board.words.push(w);
    this.state.board = this.board.words;
    this.activePlayer.rack.tiles.length = 0;
    for (const t of snap.rack) this.activePlayer.rack.tiles.push(t);
  }

  _afterMove(record = true) {
    this.bus.emit(EVENTS.BOARD_CHANGED, { state: this.state });
    const validation = this.validateCurrentBoard();
    this.bus.emit(EVENTS.WORD_VALIDATED, validation);
    this._emitState();
  }

  _emitState() {
    this.bus.emit(EVENTS.STATE_CHANGED, { state: this.state });
  }

  _currentValidWordTexts() {
    const out = [];
    for (const w of this.board.words) {
      const res = this.validator.validateWord(w.tiles);
      if (res.valid) out.push(res.word);
    }
    return out;
  }

  _boardPositionSignature() {
    const sig = {};
    this.board.words.forEach((w, wi) => {
      w.tiles.forEach((t, ti) => {
        sig[t.instanceId] = `${wi}:${ti}`;
      });
    });
    return sig;
  }

  /** Fichas que estaban en la mano al empezar el turno y ahora están en el tablero. */
  _playedTilesThisTurn() {
    const startRack = new Set(this.state.turnStart.rackIds);
    return this.board.allTiles().filter((t) => startRack.has(t.instanceId));
  }

  /** Nº de fichas que ya estaban en el tablero y han cambiado de posición. */
  _reusedTileCount() {
    const startSig = this.state.turnStart.positions;
    const nowSig = this._boardPositionSignature();
    let count = 0;
    for (const id of Object.keys(startSig)) {
      if (nowSig[id] && nowSig[id] !== startSig[id]) count++;
    }
    return count;
  }

  /** Fichas de la mano al inicio que ya no están ni en la mano ni en el tablero (comodines consumidos). */
  _consumedFromHandCount() {
    const rackNow = new Set(this.activePlayer.rack.tiles.map((t) => t.instanceId));
    const boardNow = new Set(this.board.allTiles().map((t) => t.instanceId));
    return this.state.turnStart.rackIds.filter((id) => !rackNow.has(id) && !boardNow.has(id)).length;
  }

  _boardChangedThisTurn() {
    const startRack = new Set(this.state.turnStart.rackIds);
    const nowRack = new Set(this.activePlayer.rack.tiles.map((t) => t.instanceId));
    if (startRack.size !== nowRack.size) return true;
    for (const id of startRack) if (!nowRack.has(id)) return true;
    // También si se reorganizaron fichas del tablero.
    return this._reusedTileCount() > 0;
  }

  _checkEndGame() {
    // 1) Alguien se quedó sin fichas.
    if (this.players.some((p) => p.finished)) {
      this._endGame();
      return true;
    }
    // 2) Todos los atriles vacíos (y saco vacío).
    if (this._noMovesPossibleGlobal()) {
      this._endGame();
      return true;
    }
    // 3) Bloqueo: saco vacío y todos los jugadores activos han pasado seguido.
    const activeCount = this.players.filter((p) => !p.finished).length;
    if (this.state.bag.length === 0 && this.state.consecutivePasses >= activeCount) {
      this._endGame();
      return true;
    }
    return false;
  }

  _noMovesPossibleGlobal() {
    // Si el saco está vacío y a nadie le quedan fichas normales (solo comodines o nada).
    if (this.state.bag.length > 0) return false;
    return this.players.every((p) => p.rack.isEffectivelyEmpty());
  }

  _endGame() {
    if (this.state.finished) return;
    this.state.finished = true;

    // Penalización competitiva: resta el valor de las fichas restantes en mano.
    // Solo aplica en partidas con rival (no en retos en solitario: diario/supervivencia).
    if (this.players.length > 1) {
      for (const p of this.players) {
        if (!p.rack.isEmpty()) p.addScore(-p.rack.remainingValue());
      }
    }

    // Ganador: quien se quedó sin fichas; si no, mayor puntuación.
    const finisher = this.players.find((p) => p.finished);
    let winner;
    if (finisher) {
      winner = finisher;
    } else {
      winner = [...this.players].sort((a, b) => b.score - a.score)[0];
    }
    this.state.winnerId = winner.id;

    // Bonus por GANAR: puntos extra al vencedor (solo en partidas con rival, no en solitario).
    if (this.players.length > 1) {
      const bonus = this.config.scoring?.winBonus ?? 25;
      winner.addScore(bonus);
      this.state.winBonus = bonus;
    }

    const summary = this._buildSummary(winner);
    summary.winBonus = this.state.winBonus || 0;
    this.bus.emit(EVENTS.GAME_END, summary);
  }

  _buildSummary(winner) {
    // Estadísticas de la partida a partir del historial.
    let longestWord = '';
    let bestEvolution = null;
    let maxCombo = 0;
    let totalWords = 0;
    for (const h of this.state.history) {
      for (const w of h.newWords) {
        totalWords++;
        if (letterCount(w) > letterCount(longestWord)) longestWord = w;
      }
      if (h.newWords.length > maxCombo) maxCombo = h.newWords.length;
      if (h.evolutions.length && !bestEvolution) bestEvolution = h.evolutions[0];
    }
    return {
      winnerId: winner.id,
      winnerName: winner.name,
      players: this.players.map((p) => ({ id: p.id, name: p.name, score: p.score, avatar: p.avatar })),
      longestWord,
      bestEvolution,
      maxCombo,
      totalWords,
      state: this.state,
    };
  }
}
